import { Box, CircularProgress, Container, Stack } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { FormProvider, } from "react-hook-form";
import { useTranslation } from "react-i18next";

import useAnalyticsData from "./hook/useAnalyticsData";
import DateField from "../../components/atoms/DateField";
import SelectField from "../../components/atoms/SelectField";
import LeadsAccumulatedGraph from "../../components/charts/LeadsAccumulatedGraph";
import LeadsBarChart from "../../components/charts/LeadsBarChart";
import LeadsMultipleBarChart from "../../components/charts/LeadsMultipleBarChart";
import LeadsPieChart from "../../components/charts/LeadsPieChart";
import LeadsStackBarChart from "../../components/charts/LeadsStackBarChart";
import { ErrorBoundary } from "../../components/ErrorBoundary";
import DataDisplayCard from "../../components/molecules/DataDisplayCard";
import ErrorBoundaryPage from "../error/ErrorBoundaryPage";

const AnalyticsPage = () => {
    const { t } = useTranslation();
    const {
        overviewData,
        pipelineGraphData,
        distributionGraphData,
        isLoadingDashboardSummary,
        isLoadingPipeline,
        isLoadingDistribution,
        isLoadingMetricsBySourceChannel,
        isLoadingMetricsByAssignedChannel,
        sourceChannelOptionsArray,
        assignedChannelOptionsArray,
        // startDate,
        // endDate,
        onStartDateChange,
        onEndDateChange,
        formMethods,
        onSourceChannelChange,
        onAssignedChannelChange,
        stackedQualificationfromSourceChannelData,
        stackedQualificationfromAssignedChannelData,
        accumulatedGraphData,
        apeBySourceChannelData,
        apeByAssignedChannelData,
        wonBySourceChannelData,
        wonByAssignedChannelData,
        wonAndAPEFromSourceChannel,
        wonAndAPEFromAssignedChannel,
        isLoadingAccumulatedData,
        hasMetricsByAssignedChannelError,
        hasMetricsBySourceChannelError,
        hasAccumulatedChartDataError,
        hasDistributionDataError,
        hasPipelineDataError
    } = useAnalyticsData();


    return (
    <ErrorBoundary fallback={<ErrorBoundaryPage/>}>
    <Container maxWidth={false} sx={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, height: "100%", marginBottom: "50px", maxWidth: "1800px" }}>
        <FormProvider {...formMethods}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr 1fr",
                        md: "1fr 1fr 1fr 1fr"
                    },
                    gap: 2,
                    marginTop: "32px",
                    mb: "16px"
                }}>
                    <Box>
                        <SelectField
                            options={sourceChannelOptionsArray || []}
                            name="filterSourceChannels"
                            label={t("platform.sourceChannel")}
                            onCustomChange={onSourceChannelChange}
                        />
                    </Box>
                    <Box>
                        <SelectField
                            options={assignedChannelOptionsArray || []}
                            name="filterAssignedChannels"
                            label={t("platform.targetChannel")}
                            onCustomChange={onAssignedChannelChange}
                        />
                    </Box>
                    <Box>
                        <DateField
                            name="startDate"
                            label={t("platform.startDate")}
                            maxDate
                            onCustomChange={onStartDateChange}
                        />

                    </Box>
                    <Box>
                        <DateField
                            name="endDate"
                            label={t("platform.endDate")}
                            maxDate
                            dateNotBefore={"startDate"}
                            onCustomChange={onEndDateChange}
                        />
                    </Box>

                </Box>
            </LocalizationProvider>
            <Box sx={{
                display: "grid",
                gridTemplateColumns: {
                    xs: "repeat(1, 1fr)",      // 2 columns on mobile
                    sm: "repeat(2, 1fr)",      // 3 columns on small screens
                    md: "repeat(4, 1fr)",      // 4 columns on medium screens
                    lg: "repeat(6, 1fr)",      // 6 columns on large screens (all cards in one row)
                    xl: "repeat(6, 1fr)"       // 6 columns on extra large screens
                },
                gap: 2,
                width: "100%",
                mb: 3
            }}>
                {
                    isLoadingDashboardSummary ?
                        <Box sx={{
                            gridColumn: "1 / -1", // Span all columns
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "150px"
                        }}>
                            <CircularProgress size={24} />
                        </Box>
                        : (
                            overviewData.map((item, index) => {
                                return (
                                    <DataDisplayCard
                                        key={index}
                                        icon={item.icon}
                                        title={item.label}
                                        value={item.value}
                                    />
                                )
                            })
                        )
                }
            </Box>
            <Stack>
                <LeadsAccumulatedGraph
                    xAxisData={accumulatedGraphData.xAxisData}
                    yAxisData={accumulatedGraphData.yAxisData}
                    title={t("platform.accumulatedLeads")}
                    hasError={hasAccumulatedChartDataError}
                    isLoading={isLoadingAccumulatedData}
                />
            </Stack>
            <Stack direction={"row"} gap={2}>
                <LeadsStackBarChart
                    data={stackedQualificationfromSourceChannelData}
                    isLoading={isLoadingMetricsBySourceChannel}
                    title={t("platform.qualificationBySourceChannel")}
                    hasError={hasMetricsBySourceChannelError}
                />
                <LeadsStackBarChart
                    data={stackedQualificationfromAssignedChannelData}
                    isLoading={isLoadingMetricsByAssignedChannel}
                    title={t("platform.qualificationByAssignedChannel")}
                    hasError={hasMetricsByAssignedChannelError}
                />
            </Stack>
            <Stack direction={"row"} gap={2}>
                <LeadsBarChart
                    data={pipelineGraphData}
                    isLoading={isLoadingPipeline}
                    title={t("platform.pipelineOverview")}
                    hasError={hasPipelineDataError}
                />
                <LeadsPieChart
                    data={distributionGraphData}
                    isLoading={isLoadingDistribution}
                    title={t("platform.leadDistributionType")}
                    hasError={hasDistributionDataError}
                />
            </Stack>
            <Stack direction={"row"} gap={2}>
                <LeadsBarChart
                    data={wonBySourceChannelData}
                    isLoading={isLoadingMetricsBySourceChannel}
                    title={t("platform.wonBySourceChannel")}
                    color="#4caf50"
                    hasError={hasMetricsBySourceChannelError}

                />
                <LeadsBarChart
                    data={wonByAssignedChannelData}
                    isLoading={isLoadingMetricsByAssignedChannel}
                    title={t("platform.wonByAssignedChannel")}
                    color="#e87722"
                    hasError={hasMetricsByAssignedChannelError}
                />
            </Stack>
            <Stack direction={"row"} gap={2}>
                <LeadsMultipleBarChart
                    data={wonAndAPEFromSourceChannel}
                    isLoading={isLoadingMetricsBySourceChannel}
                    title={t("platform.distributionWonDealsAPEBySourceChannel")}
                    hasError={hasMetricsBySourceChannelError}
                />
                <LeadsMultipleBarChart
                    data={wonAndAPEFromAssignedChannel}
                    isLoading={isLoadingMetricsByAssignedChannel}
                    colors={["#FEBC2C", "#0097A9"]}
                    title={t("platform.distributionWonAPEByAssignedChannel")}
                    hasError={hasMetricsByAssignedChannelError}
                />
            </Stack>
            <Stack direction={"row"} gap={2}>
                <LeadsBarChart
                    data={apeBySourceChannelData}
                    isLoading={isLoadingMetricsBySourceChannel}
                    title={t("platform.apeBySourceChannel")}
                    color="#FEBC2C"
                    hasError={hasMetricsBySourceChannelError}

                />
                <LeadsBarChart
                    data={apeByAssignedChannelData}
                    isLoading={isLoadingMetricsByAssignedChannel}
                    title={t("platform.apeByAssignedChannel")}
                    color="#0097A9"
                    hasError={hasMetricsByAssignedChannelError}
                />
            </Stack>
        </FormProvider>
    </Container>
    </ErrorBoundary>
    )
}

export default AnalyticsPage;